import { Columns } from 'lucide-react';
import {
  AgnesButton,
  AgnesDropdownMenu,
  AgnesDropdownMenuTrigger,
  AgnesDropdownMenuContent,
  AgnesDropdownMenuLabel,
  AgnesDropdownMenuSeparator,
  AgnesDropdownMenuCheckboxItem,
} from '@agnes/ui';

export interface ColumnsMenuColumn {
  key: string;
  header: string;
  /** Quando false, a coluna é estrutural e não aparece na lista de opções. */
  toggleable?: boolean;
}

export interface ColumnsMenuProps {
  columns: ColumnsMenuColumn[];
  hiddenColumnKeys: Set<string> | string[];
  onToggleColumn: (key: string, visible: boolean) => void;
  triggerLabel?: string;
  'data-qa'?: string;
}

/**
 * Substituto local do AgnesColumnsMenu enquanto o comportamento não é corrigido no
 * design system (CON-5219). Mantém o visual e a API idênticos ao AgnesColumnsMenu.
 * - Bug 1 (menu fecha a cada seleção): onSelect={preventDefault} nos itens mantém o
 *   menu aberto ao (des)marcar; fecha só no click-outside/esc (padrão do Radix).
 * - Bug 2 (tooltip preso): tooltip só por :hover (CSS), sem foco/JS, então nunca fica
 *   preso ao abrir/fechar o menu. Visual espelha o AgnesTooltip da plataforma.
 * TODO: voltar ao AgnesColumnsMenu oficial quando o @agnes/ui expuser esse comportamento.
 */
export function ColumnsMenu({
  columns,
  hiddenColumnKeys,
  onToggleColumn,
  triggerLabel = 'Colunas',
  'data-qa': dataQa,
}: Readonly<ColumnsMenuProps>) {
  const hidden = hiddenColumnKeys instanceof Set ? hiddenColumnKeys : new Set(hiddenColumnKeys);
  const toggleable = columns.filter((col) => col.toggleable !== false);

  return (
    <AgnesDropdownMenu>
      <span className="group relative inline-flex">
        <AgnesDropdownMenuTrigger asChild>
          <AgnesButton variant="outlined" size="icon" aria-label={triggerLabel} data-qa={dataQa}>
            <Columns className="h-4 w-4" />
          </AgnesButton>
        </AgnesDropdownMenuTrigger>
        {/* Tooltip por hover (CSS) — sem foco/JS, some no mouse-out. Espelha o AgnesTooltip. */}
        <span
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 z-[9999] mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-agnes-neutral-high-dark px-3 py-1.5 text-xs text-white opacity-0 shadow-agnes-panel transition-opacity duration-150 group-hover:opacity-100"
        >
          {triggerLabel}
          <span className="absolute left-1/2 top-full -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-agnes-neutral-high-dark" />
        </span>
      </span>
      {/* max-h + overflow-y-auto: rola quando há muitas colunas (mesmo comportamento do menu de Filtros). CON-5294 */}
      <AgnesDropdownMenuContent align="end" className="w-56 max-h-[60vh] overflow-y-auto">
        <AgnesDropdownMenuLabel data-qa={dataQa ? `${dataQa}-label` : undefined}>
          Colunas visíveis
        </AgnesDropdownMenuLabel>
        <AgnesDropdownMenuSeparator />
        {toggleable.map((col) => (
          <AgnesDropdownMenuCheckboxItem
            key={col.key}
            checked={!hidden.has(col.key)}
            onCheckedChange={(checked) => onToggleColumn(col.key, checked)}
            onSelect={(e) => e.preventDefault()}
            data-qa={dataQa ? `${dataQa}-item-${col.key}` : undefined}
          >
            {col.header}
          </AgnesDropdownMenuCheckboxItem>
        ))}
      </AgnesDropdownMenuContent>
    </AgnesDropdownMenu>
  );
}
